import { createElement } from 'react';
import { loadableReady } from '@loadable/component';
import { hydrateRoot as reactHydrateRoot, HydrationOptions, Root } from 'react-dom/client';
import { renderRoot } from './renderRoot';
import { ModulesProviders } from './providers';

/**
 * Опции для гидрации корневого элемента React-приложения
 */
interface HydrateRootOptions extends HydrationOptions {
    /**
     * Этот флаг нужен только для того, чтобы уметь с нуля перерисовывать ssr верстку без моргания интерфейса,
     * когда в сервисе не работает гидрация серверной верстки.
     * @default false
     */
    forceReRender?: boolean;
}

/**
 * Выполняет гидрацию серверного HTML с React-компонентами на клиенте.
 *
 * Функция ожидает загрузки всех динамических компонентов через `@loadable/component`,
 * затем выполняет гидрацию или полный рендеринг в зависимости от опций.
 * Автоматически оборачивает элемент в провайдеры для рендера модулей.
 *
 * @param container - DOM-элемент, в котором будет выполнена гидрация
 * @param element - React-элемент для гидрации
 * @param options - Опции гидрации
 * @returns Promise, который разрешается в React Root после завершения гидрации
 *
 * @example
 * ```tsx
 * import { hydrateRoot } from '@adv-frontend/modules-render/client';
 * import App from './App';
 *
 * const container = document.getElementById('root');
 * hydrateRoot(container, <App />).then(root => {
 *   console.log('Гидрация завершена');
 * });
 * ```
 */
export function hydrateRoot(container: Element, element: JSX.Element, options?: HydrateRootOptions) {
    const wrappedElement = createElement(ModulesProviders, null, element);

    return new Promise<Root>(resolve => {
        loadableReady(() => {
            if (options?.forceReRender) {
                const root = renderRoot(container, wrappedElement, options);
                resolve(root);
            } else {
                const root = reactHydrateRoot(container, wrappedElement, options);
                resolve(root);
            }
        });
    });
}
